import { fieldConfig } from "@autoform/zod";
import { z } from "zod";

// The reach label Knock targets Just Announced alerts by. Tina's `signals` stage
// writes it to `metadata.notificationRules.locality` on every event it creates;
// this is the same label for an event a curator makes or edits by hand. Without
// it a hand-made event had no label, and Knock sent it to every follower
// everywhere. `local` is the default, as it is in Tina's own rules.
export const REACH_OPTIONS = [
  "local",
  "regional",
  "national",
  "continental",
  "global",
] as const;

export type Reach = (typeof REACH_OPTIONS)[number];

export const reachField = z
  .enum(REACH_OPTIONS)
  .default("local")
  .superRefine(
    fieldConfig({
      label: "Reach",
      description:
        "Who gets the Just Announced alert. local = this city, regional = city + ~2 h around, national = the whole country, continental = people cross borders for it, global = flagship events people fly in for (rare).",
    }),
  );

type MetadataLike = Record<string, unknown> | null | undefined;

/** The stored label, or `local` when the event has none (or an unknown one). */
export function readReach(metadata: MetadataLike): Reach {
  const rules = metadata?.notificationRules as
    | Record<string, unknown>
    | undefined;
  const value = rules?.locality;
  return REACH_OPTIONS.includes(value as Reach) ? (value as Reach) : "local";
}

/**
 * Fold the form's `reach` into `metadata.notificationRules.locality` and drop the
 * form-only `reach` key. Every other `metadata` and `notificationRules` key rides
 * through: the backend full-replaces `metadata` with what IMS sends.
 */
export function withReach<T extends { reach?: Reach; metadata?: unknown }>(
  data: T,
): Omit<T, "reach"> {
  const { reach, ...rest } = data;
  if (!reach) return rest;
  const metadata = (data.metadata ?? {}) as Record<string, unknown>;
  const rules = (metadata.notificationRules ?? {}) as Record<string, unknown>;
  return {
    ...rest,
    metadata: {
      ...metadata,
      notificationRules: { ...rules, locality: reach },
    },
  };
}
